import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { PackageStatus } from "@dugads/shared";

const STATUSES: PackageStatus[] = ["draft", "active", "archived"];

export default function PackageEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<PackageStatus>("draft");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .getPackage(id)
      .then((res) => {
        setName(res.package.name);
        setDescription(res.package.description ?? "");
        setStatus(res.package.status);
      })
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load package"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setError(null);

    try {
      await api.updatePackage(id, {
        name,
        description: description || null,
        status,
      });
      navigate(`/packages/${id}`);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to update package");
      setSaving(false);
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="package-edit" data-state="loading">Loading...</p>;

  return (
    <div className="flex flex-col gap-6" data-page="package-edit" data-state={saving ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Edit Package</h1>
        <p className="text-muted-foreground">Update package details</p>
      </div>

      <form onSubmit={handleSubmit} className="flex max-w-md flex-col gap-4" data-form="package-edit">
        {error && <p className="text-sm text-destructive" data-error>{error}</p>}

        <div className="flex flex-col gap-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            data-field="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="description">Description (optional)</Label>
          <Input
            id="description"
            data-field="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="status">Status</Label>
          <select
            id="status"
            data-field="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as PackageStatus)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-3">
          <Button type="submit" data-action="submit" disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(`/packages/${id}`)} data-action="cancel">
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
